/**
 * Shared types for the Loom-style clip player media engine.
 */

export interface ClipVideoPlayerProps {
  readonly src: string;
  readonly poster?: string | null;
  readonly autoPlay?: boolean;
  /**
   * Viewer seeking lock: hides the progress bar and the skip buttons and
   * ignores every seek input (scrubber, arrows, skip). For clips whose file
   * only decodes linearly.
   */
  readonly seekingDisabled?: boolean;
  /** Extra classes on the outer frame (e.g. corner radius overrides). */
  readonly className?: string;
  readonly onError?: () => void;
  readonly onLoadedData?: () => void;
}

/** Element-synced media snapshot — source of truth for chrome derivation. */
export interface ClipMediaSnapshot {
  readonly paused: boolean;
  readonly ended: boolean;
  readonly seeking: boolean;
  /** Sticky until playing/canplay/seeked clears it; ignored for center-play UI. */
  readonly waiting: boolean;
  readonly readyState: number;
  readonly currentTime: number;
  readonly duration: number;
  readonly bufferedEnd: number;
  readonly volume: number;
  readonly muted: boolean;
  readonly playbackRate: number;
  readonly started: boolean;
  readonly hasError: boolean;
}

export interface ClipMediaUi {
  readonly showSpinner: boolean;
  readonly showCenterPlay: boolean;
  readonly displayTime: number;
  readonly progressRatio: number;
  readonly bufferedRatio: number;
  readonly effectiveVolume: number;
}

/** HTMLMediaElement.HAVE_METADATA */
export const HAVE_METADATA = 1;
/** HTMLMediaElement.HAVE_FUTURE_DATA */
export const HAVE_FUTURE_DATA = 3;

export const PLAYBACK_RATES = [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2] as const;
export const SKIP_SECONDS = 5;
export const CONTROLS_HIDE_DELAY_MS = 2600;
export const SCRUB_SEEK_THROTTLE_MS = 100;

export const EMPTY_CLIP_MEDIA_SNAPSHOT: ClipMediaSnapshot = {
  paused: true,
  ended: false,
  seeking: false,
  waiting: false,
  readyState: 0,
  currentTime: 0,
  duration: 0,
  bufferedEnd: 0,
  volume: 1,
  muted: false,
  playbackRate: 1,
  started: false,
  hasError: false,
};
